/**
 * PasteV2EntitiesCommand - Undoable command for pasting V2 StepNodes onto the canvas
 *
 * Implements the Command pattern for V2 paste operations:
 * - execute(): Adds all prepared steps to the V2 coordinator (which
 *   handles registry registration, canvas node creation, and Firebase
 *   persistence internally via `addPreparedScreens`).
 * - undo(): Deletes all pasted steps via `deleteEntities`.
 *
 * Steps must be fully prepared (new IDs, positions, edges rewritten,
 * condition keys remapped) before being passed to this command. Use
 * `V2StepPasteProcessor` for preparation.
 *
 * The coordinator is typed against a minimal structural interface
 * (`V2PasteCoordinator`) that is satisfied by BOTH
 * `V2ScreenFlowCoordinator` (regular onboarding) and
 * `StrategyV2ScreenFlowCoordinator` (strategy block screens) — they
 * both already expose `addPreparedScreens(steps)` and
 * `deleteEntities(ids)` with compatible signatures, so no coordinator
 * changes are required.
 */

import { Command, CommandResult } from './Command';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FunnelVariable } from '@segmently/schema-models/v2';

/**
 * Minimal coordinator interface required by PasteV2EntitiesCommand.
 * Both V2ScreenFlowCoordinator and StrategyV2ScreenFlowCoordinator
 * already implement this shape — see their `addPreparedScreens` and
 * `deleteEntities` methods.
 */
export interface V2PasteCoordinator {
  addPreparedScreens(steps: FlowStepNode[]): Promise<void>;
  deleteEntities(ids: string[]): Promise<unknown>;
}

export interface V2PasteVariablePersistence {
  saveVariables(variables: FunnelVariable[]): Promise<void>;
  deleteVariables(variableIds: string[]): Promise<void>;
}

export interface PasteV2EntitiesCommandOptions {
  variablesToCreate?: FunnelVariable[];
  variablePersistence?: V2PasteVariablePersistence;
}

/**
 * PasteV2EntitiesCommand - Paste pre-processed V2 steps onto the canvas
 */
export class PasteV2EntitiesCommand implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private pastedEntityIds: string[] = [];
  private createdVariableIds: string[] = [];

  /**
   * @param preparedSteps - Fully processed V2 steps (from V2StepPasteProcessor)
   * @param coordinator - V2 flow coordinator for canvas/Firebase operations
   */
  constructor(
    private preparedSteps: FlowStepNode[],
    private coordinator: V2PasteCoordinator,
    private options: PasteV2EntitiesCommandOptions = {},
  ) {
    this.id = `paste-v2-entities-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.description = `Paste ${preparedSteps.length} V2 screen(s)`;
    this.timestamp = Date.now();
  }

  /**
   * Execute paste — add all prepared V2 steps to canvas and Firebase.
   *
   * Flow:
   * 1. Call coordinator.addPreparedScreens() which handles:
   *    - Registry registration (v2ScreenRegistry)
   *    - Canvas node creation (ReactFlow)
   *    - Firebase persistence (per-step add with edge sync at the end)
   * 2. Track pasted entity IDs for undo
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log(`[PasteV2EntitiesCommand] Pasting ${this.preparedSteps.length} V2 screen(s)`);

      const variablesToCreate = this.options.variablesToCreate || [];
      if (variablesToCreate.length > 0) {
        if (!this.options.variablePersistence) {
          throw new Error('Variable persistence is required when pasting variable dependencies');
        }
        await this.options.variablePersistence.saveVariables(variablesToCreate);
        this.createdVariableIds = variablesToCreate.map(variable => variable.id);
      }

      await this.coordinator.addPreparedScreens(this.preparedSteps);

      this.pastedEntityIds = this.preparedSteps.map(s => s.entityId);

      console.log(
        `[PasteV2EntitiesCommand] ✅ Pasted: ${this.pastedEntityIds.join(', ').slice(0, 100)}`
      );

      return {
        success: true,
        affectedEntities: { screens: [...this.pastedEntityIds] },
      };
    } catch (error) {
      if (this.pastedEntityIds.length === 0 && this.createdVariableIds.length > 0) {
        try {
          await this.options.variablePersistence?.deleteVariables(this.createdVariableIds);
          this.createdVariableIds = [];
        } catch (cleanupError) {
          console.warn('[PasteV2EntitiesCommand] Variable cleanup after failed paste failed:', cleanupError);
        }
      }

      console.error('[PasteV2EntitiesCommand] ❌ Paste failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {},
      };
    }
  }

  /**
   * Undo paste — delete all pasted V2 steps.
   */
  async undo(): Promise<CommandResult> {
    if (this.pastedEntityIds.length === 0) {
      return {
        success: false,
        error: new Error('No pasted entities to undo'),
        affectedEntities: {},
      };
    }

    try {
      console.log(
        `[PasteV2EntitiesCommand] Undoing paste of ${this.pastedEntityIds.length} V2 screen(s)`
      );

      await this.coordinator.deleteEntities(this.pastedEntityIds);
      if (this.createdVariableIds.length > 0) {
        await this.options.variablePersistence?.deleteVariables(this.createdVariableIds);
      }

      console.log('[PasteV2EntitiesCommand] ✅ Undo successful');

      const undoneScreenIds = [...this.pastedEntityIds];
      this.pastedEntityIds = [];
      this.createdVariableIds = [];

      return {
        success: true,
        affectedEntities: { screens: undoneScreenIds },
      };
    } catch (error) {
      console.error('[PasteV2EntitiesCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {},
      };
    }
  }

  canExecute(): boolean {
    return this.preparedSteps.length > 0;
  }

  canUndo(): boolean {
    return this.pastedEntityIds.length > 0;
  }
}
